import { EventType } from "@/analytics/analytics-events";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";

type ImportDataDialogContextType = {
  /** Whether the import data dialog is open */
  isOpen: boolean;
  /** Opens the import data dialog */
  openDialog(): void;
  /** Closes the import data dialog */
  closeDialog(): void;
};

const ImportDataDialogContext = createContext<
  ImportDataDialogContextType | undefined
>(undefined);

/**
 * Provider for managing the import data dialog state
 *
 * @returns The provider component
 */
export function ImportDataDialogProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const openDialog = useCallback(() => {
    Analytics.track(EventType.openImportDataDialog);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      openDialog,
      closeDialog,
    }),
    [isOpen, openDialog, closeDialog],
  );

  return (
    <ImportDataDialogContext value={value}>{children}</ImportDataDialogContext>
  );
}

/**
 * Hook to access the import data dialog context
 *
 * @returns The import data dialog context value
 */
export function useImportDataDialog(): ImportDataDialogContextType {
  const context = useContext(ImportDataDialogContext);
  if (!context) {
    throw new Error(
      "useImportDataDialog must be used within ImportDataDialogProvider",
    );
  }
  return context;
}
